import React, { useState } from "react";
import { AppStoreInfo } from "./types";
import { fmtN, pctOf } from "./format";
import { Delta } from "./Primitives";

// ---- App Store card ---------------------------------------------------------

export function AppStoreCard({
  info,
  appOpens,
  onSync,
  syncing,
  syncMsg,
}: {
  info: AppStoreInfo;
  appOpens: number;
  onSync: () => void;
  syncing: boolean;
  syncMsg: string | null;
}) {
  const [showLists, setShowLists] = useState(false);
  const lastSync = info.sync?.lastRunAt ? new Date(info.sync.lastRunAt).toLocaleString() : null;
  const lastError = info.sync?.lastError || null;
  const lastErrorShort = lastError && lastError.length > 200 ? `${lastError.slice(0, 200)}…` : lastError;
  const syncMsgShort = syncMsg && syncMsg.length > 200 ? `${syncMsg.slice(0, 200)}…` : syncMsg;
  const hasLists = info.bySource.length > 0 || info.byCountry.length > 0;
  const cell = (label: string, cur: number | null, prev: number | null, note?: string) => (
    <div className="gr-cell">
      <div className="gr-cell-lab">{label}</div>
      <div className="gr-cell-val">
        {cur == null ? (
          <span className="gr-muted">waiting for Apple</span>
        ) : (
          <>
            {fmtN(cur)} <Delta cur={cur} prev={prev} />
          </>
        )}
      </div>
      {note && <div className="gr-cell-sub">{note}</div>}
    </div>
  );
  return (
    <div className="adm-card gr-block">
      <h3>
        📱 App Store → app <small>· Apple's listing numbers feed the top of the app funnel · reported daily, 24–48h behind</small>
      </h3>
      {!info.configured ? (
        <div className="gr-asc-setup">
          <b>Connect App Store Connect — one time, about five minutes.</b> Listing views and downloads plug in here the moment the key
          is in Railway.
          <ol>
            <li>
              App Store Connect → <b>Users and Access</b> → <b>Integrations</b> → <b>App Store Connect API</b> → Team Keys →{" "}
              <b>Generate API Key</b>. Name it "Growth Funnel", access <b>Admin</b>. Download the <code>.p8</code> file — Apple offers it
              only once.
            </li>
            <li>
              On that same page copy the <b>Issuer ID</b> (top) and the new key's <b>Key ID</b>.
            </li>
            <li>
              In Terminal: <code>base64 -i AuthKey_XXXX.p8 | tr -d '\n' | pbcopy</code> — that copies the key as one line.
            </li>
            <li>
              Railway → server → Variables: <code>ASC_ISSUER_ID</code>, <code>ASC_KEY_ID</code>, <code>ASC_PRIVATE_KEY_P8_BASE64</code>{" "}
              (paste). Optional <code>ASC_VENDOR_NUMBER</code> — Payments and Financial Reports, top-left "Vendor #" — adds downloads by
              country.
            </li>
          </ol>
          <div className="gr-note">Missing right now: {info.missing.join(", ")}. The first sync pulls the last 30 days.</div>
          <button className="gr-sync-btn" onClick={onSync} disabled={syncing}>
            {syncing ? "Checking…" : "I've added them — check now"}
          </button>
          {syncMsgShort && (
            <div className="gr-note" title={syncMsg || undefined}>
              {syncMsgShort}
            </div>
          )}
        </div>
      ) : (
        <>
          {!info.live && (
            <div className="gr-appstore-pending">
              The listing isn't public yet — Apple starts reporting the day it goes live. The key is in place.
            </div>
          )}
          <div className="gr-asc-strip">
            {cell("Listing impressions", info.cur.impressions, info.prev.impressions)}
            {cell("Product page views", info.cur.pageViews, info.prev.pageViews, `${pctOf(info.cur.pageViews, info.cur.impressions)} of impressions`)}
            {cell("Downloads", info.cur.downloads, info.prev.downloads, `${pctOf(info.cur.downloads, info.cur.pageViews)} of page views`)}
            <div className="gr-cell">
              <div className="gr-cell-lab">App opens (ours, incl. existing users)</div>
              <div className="gr-cell-val">{fmtN(appOpens)}</div>
              <div className="gr-cell-sub">{pctOf(appOpens, info.cur.downloads)} of downloads</div>
            </div>
          </div>
          {hasLists && showLists && (
            <div className="gr-two gr-pad-top">
              {info.bySource.length > 0 && (
                <div>
                  <div className="gr-cell-lab">Where listing views come from</div>
                  {info.bySource.map((s) => (
                    <div className="gr-src-row" key={s.key}>
                      <span>{s.key}</span>
                      <span>{fmtN(s.n)}</span>
                    </div>
                  ))}
                </div>
              )}
              {info.byCountry.length > 0 && (
                <div>
                  <div className="gr-cell-lab">Downloads by country</div>
                  {info.byCountry.map((s) => (
                    <div className="gr-src-row" key={s.key}>
                      <span>{s.key}</span>
                      <span>{fmtN(s.n)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="gr-note gr-asc-status">
            {hasLists && (
              <button type="button" className="gr-link" onClick={() => setShowLists(!showLists)}>
                {showLists ? "Hide channels & countries ▾" : "Channels & countries ▸"}
              </button>
            )}
            <span title={lastError || undefined}>
              {hasLists ? " · " : ""}
              {lastSync ? `Last synced ${lastSync}` : "Not synced yet"}
              {lastErrorShort ? ` · last problem: ${lastErrorShort}` : ""}
              {!info.salesConfigured ? " · add ASC_VENDOR_NUMBER for downloads by country" : ""}
            </span>
            <button className="gr-sync-btn gr-sync-inline" onClick={onSync} disabled={syncing}>
              {syncing ? "Syncing…" : "Sync now"}
            </button>
            {syncMsgShort && <span title={syncMsg || undefined}>· {syncMsgShort}</span>}
          </div>
        </>
      )}
    </div>
  );
}
